import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsCheck2, BsChevronDown, BsClock, BsEnvelope, BsFileText, BsPlus, BsSend, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Tag from '#globalComponents/Tag/Tag';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { sanitizeHtml } from '#lib/sanitize';

import { useInsertSingleEmailOfCampaign, useUpdateSingleEmailData } from '../../../models/emailCampaignMails/mutations';
import { useEmailTemplates } from '../../../models/emailTemplates/queries';
import SendTestEmailModal from '../../common/SendTestEmailModal/SendTestEmailModal';

/* Template Dropdown */
const TemplateDropdown = ({ templates, templatesLoading, selectedTemplateId, onSelect, onClose, triggerRef }) => {
	const dropdownRef = useRef(null);

	useEffect(() => {
		const handleClickOutside = (e) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target) &&
				triggerRef?.current &&
				!triggerRef.current.contains(e.target)
			) {
				onClose();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [onClose, triggerRef]);

	return (
		<div
			ref={dropdownRef}
			className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-bgBase-500 rounded-card shadow-elevated overflow-hidden"
		>
			{templatesLoading ? (
				<div className="px-3 py-4 text-xs text-bgBase-800 text-center">Laden…</div>
			) : templates.length === 0 ? (
				<div className="px-3 py-4 text-center">
					<p className="text-sm text-bgBase-800 mb-1">Noch keine Templates vorhanden.</p>
					<a
						href="/email-campaigns/templates"
						className="text-xs text-primary-600 hover:text-primary-700 font-medium"
					>
						Templates erstellen &rarr;
					</a>
				</div>
			) : (
				<div className="max-h-[220px] overflow-y-auto py-1">
					{templates.map((tpl) => {
						const isSelected = selectedTemplateId === tpl.id;
						return (
							<button
								key={tpl.id}
								type="button"
								onClick={() => onSelect(tpl)}
								className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 transition-colors ${
									isSelected ? 'bg-primary-100 text-primary-700' : 'text-text hover:bg-bgBase-300'
								}`}
							>
								<div className="min-w-0">
									<div className="text-sm font-medium truncate">{tpl.name}</div>
									<div className="text-[11px] text-bgBase-800 truncate">{tpl.emailSubject}</div>
								</div>
								{isSelected && <BsCheck2 className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />}
							</button>
						);
					})}
				</div>
			)}
		</div>
	);
};

TemplateDropdown.propTypes = {
	templates: PropTypes.array.isRequired,
	templatesLoading: PropTypes.bool,
	selectedTemplateId: PropTypes.string,
	onSelect: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	triggerRef: PropTypes.object,
};

/* Main Component */
const EmailSequenceTimeline = ({ emails = [], campaignId, emailAccountId, listIds, onDelete, totalDuration = 0 }) => {
	const { hasCapability: canSend } = useCapabilityCheck('email.send');
	const [selectorForStepId, setSelectorForStepId] = useState(null);
	const [editingDelayStepId, setEditingDelayStepId] = useState(null);
	const [delayInputValue, setDelayInputValue] = useState('');
	// Step whose template should be sent as a test email; null closes the modal.
	const [testStep, setTestStep] = useState(null);
	const triggerRefs = useRef({});

	const { data: templates = [], isLoading: templatesLoading } = useEmailTemplates();
	const insertMutation = useInsertSingleEmailOfCampaign();
	const updateMutation = useUpdateSingleEmailData();

	const handleDelayClick = (email) => {
		setEditingDelayStepId(email.id);
		setDelayInputValue(String(email.delayDays || 0));
	};

	const handleDelaySave = async (stepId) => {
		const days = Math.max(0, parseInt(delayInputValue, 10) || 0);
		setEditingDelayStepId(null);
		await updateMutation.mutateAsync({ stepId, campaignId, updates: { delayDays: days } });
	};

	const handleDelayKeyDown = (e, stepId) => {
		if (e.key === 'Enter') handleDelaySave(stepId);
		if (e.key === 'Escape') setEditingDelayStepId(null);
	};

	const handleTemplateSelected = async (template) => {
		if (selectorForStepId && selectorForStepId !== 'new') {
			await updateMutation.mutateAsync({
				stepId: selectorForStepId,
				campaignId,
				updates: { emailTemplateId: template.id },
			});
		} else {
			await insertMutation.mutateAsync({
				campaignId,
				emailTemplateId: template.id,
				delayDays: emails.length === 0 ? 0 : 7,
			});
		}
		setSelectorForStepId(null);
	};

	const getTriggerRef = (id) => {
		if (!triggerRefs.current[id]) triggerRefs.current[id] = { current: null };
		return triggerRefs.current[id];
	};

	const calculatedDuration = emails.reduce((sum, email) => sum + (email.delayDays || 0), 0);
	const displayDuration = totalDuration || calculatedDuration;

	return (
		<div>
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-2 text-sm text-bgBase-800">
					<BsClock className="h-4 w-4" />
					<span>
						Gesamtdauer: {displayDuration} {displayDuration === 1 ? 'Tag' : 'Tage'}
					</span>
				</div>
				<Button onClick={() => setSelectorForStepId('new')} variant="outline" color="gray" size="sm">
					<BsPlus className="mr-2 h-4 w-4" /> Schritt hinzufügen
				</Button>
			</div>

			{/* Timeline */}
			<div className="relative">
				{emails.map((email, index) => {
					const isSelectingForThis = selectorForStepId === email.id;
					const ref = getTriggerRef(email.id);

					return (
						<div key={email.id || index}>
							{/* Delay indicator */}
							{index > 0 && (
								<div className="flex items-center gap-3 py-1.5 pl-[18px]">
									<div className="w-0.5 h-6 bg-bgBase-500" />
									{editingDelayStepId === email.id ? (
										<div className="inline-flex items-center gap-1.5 text-xs shrink-0 flex-none">
											<BsClock className="h-3 w-3 text-bgBase-800" />
											<input
												type="number"
												min="0"
												max="999"
												autoFocus
												value={delayInputValue}
												onChange={(e) => {
													if (e.target.value.length <= 3) setDelayInputValue(e.target.value);
												}}
												onBlur={() => handleDelaySave(email.id)}
												onKeyDown={(e) => handleDelayKeyDown(e, email.id)}
												className="no-spinners w-9 h-5 px-0.5 text-xs text-center border border-bgBase-500 rounded focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400"
											/>
											<span className="text-bgBase-800 whitespace-nowrap">Tage später</span>
										</div>
									) : (
										<button
											type="button"
											onClick={() => handleDelayClick(email)}
											className="flex items-center gap-1.5 text-xs text-bgBase-800 hover:text-text transition-colors cursor-pointer"
										>
											<BsClock className="h-3 w-3" />
											<span>
												{email.delayDays} {email.delayDays === 1 ? 'Tag' : 'Tage'} später
											</span>
										</button>
									)}
								</div>
							)}

							{/* Step */}
							<div className="relative flex gap-3">
								{/* Node */}
								<div className="flex flex-col items-center pt-3">
									<div
										className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${
											index === 0
												? 'bg-primary-600 text-white shadow-[0_0_0_3px_rgba(79,70,229,0.15)]'
												: 'bg-white border border-bgBase-500 text-bgBase-800'
										}`}
									>
										{email.stepOrder || index + 1}
									</div>
									{index < emails.length - 1 && <div className="w-0.5 flex-1 bg-bgBase-500 mt-1.5" />}
								</div>

								<div className="flex-1 mb-2 relative">
									<div className="group border border-bgBase-500 bg-white rounded-card px-4 py-3 hover:border-primary-200 transition-colors">
										<div className="flex items-start justify-between gap-3">
											<div className="flex-1 min-w-0">
												{/* Badges */}
												<div className="flex items-center gap-1.5 mb-1.5">
													{index === 0 && <Tag label="Start" size="xs" />}
													{email.templateName && (
														<span className="inline-flex items-center gap-1 text-[10px] text-bgBase-800">
															<BsFileText className="w-2.5 h-2.5" /> {email.templateName}
														</span>
													)}
												</div>

												{/* Subject */}
												<h4 className="text-sm font-medium text-text truncate">
													{email.subject || '(kein Betreff)'}
												</h4>

												{/* Preview */}
												<div className="text-xs text-bgBase-800 mt-0.5 line-clamp-1">
													{email.content ? (
														<span
															dangerouslySetInnerHTML={{
																__html: sanitizeHtml(email.content.substring(0, 120)),
															}}
														/>
													) : (
														'Kein Inhalt'
													)}
												</div>
											</div>

											<div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
												{/* Test email — only sendable once an email account is selected */}
												{emailAccountId && canSend && (
													<button
														type="button"
														onClick={() => setTestStep(email)}
														title="Test-E-Mail senden"
														className="p-1 text-bgBase-800 hover:text-primary-600 rounded-control transition-colors"
													>
														<BsSend className="w-3 h-3" />
													</button>
												)}
												<button
													type="button"
													ref={(el) => {
														ref.current = el;
													}}
													onClick={() =>
														setSelectorForStepId(isSelectingForThis ? null : email.id)
													}
													className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-control px-2 py-1 transition-colors ${
														isSelectingForThis
															? 'bg-primary-100 text-primary-600'
															: 'text-bgBase-800 hover:text-text hover:bg-bgBase-300'
													}`}
												>
													<BsEnvelope className="w-3 h-3" />
													<BsChevronDown
														className={`w-2.5 h-2.5 transition-transform ${isSelectingForThis ? 'rotate-180' : ''}`}
													/>
												</button>
												<button
													type="button"
													onClick={() => onDelete(email)}
													className="p-1 text-bgBase-800 hover:text-warning-500 rounded-control transition-colors"
												>
													<BsTrash className="w-3 h-3" />
												</button>
											</div>
										</div>
									</div>

									{/* Template dropdown */}
									{isSelectingForThis && (
										<TemplateDropdown
											templates={templates}
											templatesLoading={templatesLoading}
											selectedTemplateId={email.emailTemplateId}
											onSelect={handleTemplateSelected}
											onClose={() => setSelectorForStepId(null)}
											triggerRef={ref}
										/>
									)}
								</div>
							</div>
						</div>
					);
				})}

				{/* Add new step */}
				{selectorForStepId === 'new' && (
					<div className="flex gap-3">
						<div className="flex flex-col items-center pt-3">
							<div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 border border-dashed border-bgBase-600 text-bgBase-800">
								<BsPlus className="w-3.5 h-3.5" />
							</div>
						</div>
						<div className="flex-1 relative mb-2">
							<div className="border border-bgBase-500 bg-white rounded-card px-4 py-3">
								<div className="flex items-center justify-between mb-0.5">
									<span className="text-sm font-medium text-text">Neuer Schritt</span>
									<button
										type="button"
										onClick={() => setSelectorForStepId(null)}
										className="text-[11px] text-bgBase-800 hover:text-text transition-colors"
									>
										Abbrechen
									</button>
								</div>
								<p className="text-xs text-bgBase-800 mb-3">Template auswählen</p>
								{templatesLoading ? (
									<div className="text-xs text-bgBase-800 py-2 text-center">Laden…</div>
								) : templates.length === 0 ? (
									<div className="text-center py-3">
										<p className="text-xs text-bgBase-800 mb-1">Noch keine Templates vorhanden.</p>
										<a
											href="/email-campaigns/templates"
											className="text-xs text-primary-600 hover:text-primary-700 font-medium"
										>
											Templates erstellen &rarr;
										</a>
									</div>
								) : (
									<div className="space-y-1 max-h-[200px] overflow-y-auto">
										{templates.map((tpl) => (
											<button
												key={tpl.id}
												type="button"
												onClick={() => handleTemplateSelected(tpl)}
												className="w-full text-left px-3 py-2 rounded-control flex items-center justify-between gap-2 text-text hover:bg-bgBase-300 transition-colors"
											>
												<div className="min-w-0">
													<div className="text-sm font-medium truncate">{tpl.name}</div>
													<div className="text-[11px] text-bgBase-800 truncate">
														{tpl.emailSubject}
													</div>
												</div>
											</button>
										))}
									</div>
								)}
							</div>
						</div>
					</div>
				)}

				{/* Add button */}
				{emails.length > 0 && selectorForStepId !== 'new' && (
					<div className="flex gap-3 pt-1">
						<div className="flex flex-col items-center pt-0.5">
							<div className="w-9 h-9" />
						</div>
						<button
							type="button"
							onClick={() => setSelectorForStepId('new')}
							className="flex items-center gap-1.5 text-xs text-bgBase-800 hover:text-primary-600 transition-colors py-1"
						>
							<BsPlus className="w-3.5 h-3.5" />
							<span>Schritt hinzufügen</span>
						</button>
					</div>
				)}
			</div>

			{/* Empty state */}
			{emails.length === 0 && selectorForStepId !== 'new' && (
				<div className="flex flex-col items-center py-10">
					<div className="w-10 h-10 bg-primary-100 rounded-card grid place-items-center mb-3">
						<BsEnvelope className="w-4 h-4 text-primary-400" />
					</div>
					<h3 className="text-sm font-semibold text-text mb-0.5">E-Mail-Sequenz starten</h3>
					<p className="text-xs text-bgBase-800 mb-4">Wählen Sie ein Template für den ersten Schritt</p>
					{templatesLoading ? (
						<div className="text-xs text-bgBase-800">Laden…</div>
					) : templates.length === 0 ? (
						<a
							href="/email-campaigns/templates"
							className="text-sm text-primary-600 hover:text-primary-700 font-medium"
						>
							Erstes Template erstellen &rarr;
						</a>
					) : (
						<Button onClick={() => setSelectorForStepId('new')} variant="outline" color="gray" size="sm">
							<BsPlus className="mr-1.5 h-3.5 w-3.5" /> Template wählen
						</Button>
					)}
				</div>
			)}

			{/* Test email modal — tests the selected step's template */}
			{testStep && emailAccountId && (
				<SendTestEmailModal
					isOpen
					onClose={() => setTestStep(null)}
					emailAccountId={emailAccountId}
					campaignId={campaignId}
					campaignMailId={testStep.id}
					listIds={listIds}
				/>
			)}
		</div>
	);
};

EmailSequenceTimeline.propTypes = {
	emails: PropTypes.array,
	campaignId: PropTypes.string.isRequired,
	emailAccountId: PropTypes.string,
	// The campaign's saved contact lists; the test email's sample recipient is picked from these.
	listIds: PropTypes.arrayOf(PropTypes.string),
	onDelete: PropTypes.func.isRequired,
	totalDuration: PropTypes.number,
};

export default EmailSequenceTimeline;
